/* eslint-disable no-case-declarations,@typescript-eslint/no-redundant-type-constituents */
import { checkFavToken } from "@/store/utils";
import {
  SupStoredOverlay,
  SupStoredTxSettings,
  SupStoredViewMode,
  SupWeb3Balance,
  SupSingleVaultV3,
  SupStoredData,
  SupStoredAction,
  SupStoredWithdrawItem,
  SupStoredFavToken,
  SupStoredFilter,
  Networkish,
  SupStoredDataGridColumns,
  SupStoredRewardTokenValuations,
  SupStoredTransmute,
  SupStoredTxCartValue,
} from "@/types";
import dappConfig from "@/../config/dapp.config";

const windowRef = global.window as unknown as Window | undefined;

const typeDic = {
  cart: "deposit-cart",
  withdrawCart: "withdraw-cart",
  favorites: "favorites",
  vaultsWithErrors: "vaults-with-errors",
  withdrawalChainId: "withdrawal-chain-id",
  overlay: "current-overlay",
  favTokens: "fav-tokens",
  filters: "grid-filters",
  viewMode: "view-mode",
  showDust: "show-dust",
  filterFavorites: "show-favorites",
  depositBalance: "deposit-balance",
  txSettings: "tx-flow-settings",
  dataGridColumns: "data-grid-columns",
  tokenSelectorChains: "token-selector-chains",
  includeRewardsInApy: "include-rewards-in-apy",
  rewardTokenValuations: "reward-token-valuations",
  transmute: "transmute",
  txCartValue: "tx-cart-value",
};

/**
 * Wrapper to persist data-storage in window.localStorage
 *
 * @uses Window.localStorage
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage
 *
 * @param {string[] | string | SupSingleVaultV3 | undefined | SupStoredWithdrawItem} data
 * @param {"cart" | "favorites" | "vaultsWithErrors"} type
 * @param storeBoolean
 */
const storagePush = (
  data:
    | string[]
    | string
    | SupSingleVaultV3
    | undefined
    | SupStoredWithdrawItem[]
    | SupStoredFavToken[]
    | SupStoredFilter[]
    | SupStoredViewMode
    | boolean
    | SupWeb3Balance
    | Networkish
    | Networkish[]
    | SupStoredTxSettings
    | SupStoredDataGridColumns[]
    | SupStoredOverlay
    | SupStoredRewardTokenValuations[]
    | SupStoredTransmute
    | SupStoredTxCartValue,
  type:
    | "cart"
    | "txSettings"
    | "favorites"
    | "vaultsWithErrors"
    | "withdrawCart"
    | "overlay"
    | "withdrawalChainId"
    | "favTokens"
    | "filters"
    | "viewMode"
    | "showDust"
    | "filterFavorites"
    | "depositBalance"
    | "dataGridColumns"
    | "tokenSelectorChains"
    | "includeRewardsInApy"
    | "rewardTokenValuations"
    | "transmute"
    | "txCartValue",
  storeBoolean = false
) => {
  if (!data && !storeBoolean) {
    windowRef?.localStorage.removeItem(`${dappConfig.storePrefix}-${typeDic[type]}`);
  } else {
    windowRef?.localStorage.setItem(`${dappConfig.storePrefix}-${typeDic[type]}`, typeof data === "string" ? data : JSON.stringify(data));
  }
};

export const defaultTxSettings: SupStoredTxSettings = {
  bridgeSlippage: 50,
  dexSlippage: 50,
  routePriority: "output",
  services: {
    ambs: [],
    dexes: [],
    bridges: [],
    liquidity_providers: [],
  },
  retain4626: false,
  refundAddress: undefined,
};

const storedFavorites = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.favorites}`);
const vaultsWithError = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.vaultsWithErrors}`);
const storedCart = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.cart}`);
const withdrawalChainId = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.withdrawalChainId}`);
const storedWithdrawCart = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.withdrawCart}`);
const storedFavTokens = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.favTokens}`);
const storedFilters = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.filters}`);
const storedShowDust = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.showDust}`);
const storedFilterFavorites = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.filterFavorites}`);
const storedViewMode = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.viewMode}`);
const storedDepositBalance = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.depositBalance}`);
const storedTxSettings = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.txSettings}`);
const storedDataGridColumns = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.dataGridColumns}`);
const storedTokenSelectorChains = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.tokenSelectorChains}`);
const storedIncludeRewardsInApy = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.includeRewardsInApy}`);
const storedRewardTokenValuations = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.rewardTokenValuations}`);
const storedTransmute = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.transmute}`);
const storedTxCartValue = windowRef?.localStorage.getItem(`${dappConfig.storePrefix}-${typeDic.txCartValue}`);

export const StoreDataInitial: SupStoredData = {
  vaultsWithErrors: ((vaultsWithError && JSON.parse(vaultsWithError)) || []) as string[],
  cart: ((storedCart && JSON.parse(storedCart)) || []) as string[],
  withdrawCart: ((storedWithdrawCart && JSON.parse(storedWithdrawCart)) || []) as SupStoredWithdrawItem[],
  favorites: ((storedFavorites && JSON.parse(storedFavorites)) || []) as string[],
  withdrawalChainId: withdrawalChainId ? (parseInt(withdrawalChainId) as Networkish) : undefined,
  overlay: undefined,
  favTokens: ((storedFavTokens && JSON.parse(storedFavTokens)) || []) as SupStoredFavToken[],
  filters: ((storedFilters && JSON.parse(storedFilters)) || [{ name: "unauthenticated", values: false }]) as SupStoredFilter[],
  viewMode: (storedViewMode ? storedViewMode : "list") as SupStoredViewMode,
  showDust: storedShowDust ? Boolean(storedShowDust) : (false as boolean),
  filterFavorites: storedFilterFavorites ? Boolean(storedFilterFavorites) : (false as boolean),
  depositBalance: storedDepositBalance ? (JSON.parse(storedDepositBalance) as SupWeb3Balance) : undefined,
  txSettings: storedTxSettings ? (JSON.parse(storedTxSettings) as SupStoredTxSettings) : defaultTxSettings,
  dataGridColumns: (storedDataGridColumns ? JSON.parse(storedDataGridColumns) : []) as SupStoredDataGridColumns[],
  tokenSelectorChains: (storedTokenSelectorChains ? JSON.parse(storedTokenSelectorChains) : []) as Networkish[],
  includeRewardsInApy: storedIncludeRewardsInApy ? storedIncludeRewardsInApy !== "false" : true,
  rewardTokenValuations: (storedRewardTokenValuations ? JSON.parse(storedRewardTokenValuations) : []) as SupStoredRewardTokenValuations[],
  transmute: (storedTransmute ? JSON.parse(storedTransmute) : undefined) as SupStoredTransmute,
  txCartValue: (storedTxCartValue ? JSON.parse(storedTxCartValue) : undefined) as SupStoredTxCartValue | undefined,
};

/**
 * Initialize global reducer
 * @param {SupStoredData} initialStoredParam
 * @return {SupStoredData}
 */
export const StoreDataInit = (initialStoredParam?: SupStoredData): SupStoredData => {
  return initialStoredParam ?? StoreDataInitial;
};

/**
 * Reducer action method
 *
 * @param {SupStoredData} state
 * @param props
 * @return {SupStoredData}
 */
export const StoreDataReducer = (state: SupStoredData, props: SupStoredAction): SupStoredData => {
  const {
    type,
    vault,
    balance,
    overlay,
    favToken,
    filter,
    filterName,
    viewMode,
    depositBalance,
    txSettings,
    dataGridColumns,
    tokenSelectorChains,
    grid,
    rewardTokenValuations,
    transmute,
    txCartValue,
  } = props;

  const vaultSlug = vault ? vault.slug : "";

  switch (type) {
    case "storeVaultError":
      if (!state.vaultsWithErrors.includes(vaultSlug)) {
        const updatedVaultsWithError = [...state.vaultsWithErrors, vaultSlug];
        storagePush(updatedVaultsWithError, "vaultsWithErrors");
        return {
          ...state,
          vaultsWithErrors: updatedVaultsWithError,
        };
      }
      return state;

    case "toggleCart":
      const newCartData = state.cart.includes(vaultSlug) ? state.cart.filter(single => single !== vaultSlug) : [...state.cart, vaultSlug];
      storagePush(newCartData, "cart");

      return {
        ...state,
        cart: newCartData,
      } as SupStoredData;

    case "setDepositBalance":
      const valueToStore = depositBalance ?? undefined;
      storagePush(valueToStore, "depositBalance");

      return {
        ...state,
        depositBalance: valueToStore,
      } as SupStoredData;

    case "toggleWithdrawCart":
      /**
       * Validating the balance
       */
      if (!balance) {
        throw new Error("Balance is required for toggleWithdrawCart");
      }
      /**
       * Validating the vault
       */
      if (!vault) {
        throw new Error("Vault is required for toggleWithdrawCart");
      }

      /**
       * Validating the chainId to match currently stored withdrawalChainId
       */
      if (state.withdrawalChainId !== undefined && state.withdrawalChainId !== balance.chain_id && state.withdrawCart.length > 0) {
        throw new Error("Cannot add balances with the different chains to withdraw cart");
      }

      const newWithdrawCartData = state.withdrawCart.find(withdrawItem => {
        return withdrawItem.vault_id === vault.id && withdrawItem.chainId === balance.chain_id && withdrawItem.is_erc20 === balance.is_erc20;
      })
        ? state.withdrawCart.filter(single => single.vault_id !== vault.id || single.chainId !== balance.chain_id || single.is_erc20 !== balance.is_erc20)
        : ([
            ...state.withdrawCart,
            {
              vault_id: vault.id,
              superform_id: balance.superposition_id,
              balance: balance.superposition_balance,
              chainId: balance.chain_id,
              is_erc20: balance.is_erc20,
            },
          ] as SupStoredWithdrawItem[]);

      /**
       * Storing the withdrawal cart
       */
      storagePush(newWithdrawCartData, "withdrawCart");

      /**
       * Storing the chainId to avoid multiple chain withdrawals in a single cart
       */
      const newWithdrawalChainId = newWithdrawCartData.length === 0 ? undefined : balance.chain_id;
      storagePush(newWithdrawalChainId, "withdrawalChainId");

      return {
        ...state,
        withdrawCart: newWithdrawCartData,
        withdrawalChainId: newWithdrawalChainId,
      } as SupStoredData;

    case "removeWithdrawCart":
      /**
       * Validating the chainId to match currently stored withdrawalChainId
       */
      if (state.withdrawCart.length < 1) {
        throw new Error("Withdraw cart is empty");
      }

      const foundItem = state.withdrawCart.find(withdrawItem => {
        return withdrawItem.vault_id === vault?.id;
      });
      if (!foundItem) {
        throw new Error("Item is missing in the withdrawal cart");
      }
      const afterRemovalWithdrawCartData = state.withdrawCart.filter(single => single.vault_id !== vault?.id);

      /**
       * Storing the chainId to avoid multiple chain withdrawals in a single cart
       */
      if (afterRemovalWithdrawCartData.length === 0) {
        storagePush(undefined, "withdrawalChainId");
      }
      /**
       * Storing the withdrawal cart
       */
      storagePush(afterRemovalWithdrawCartData, "withdrawCart");

      return {
        ...state,
        withdrawCart: afterRemovalWithdrawCartData,
        withdrawalChainId: afterRemovalWithdrawCartData.length === 0 ? undefined : state.withdrawalChainId,
      } as SupStoredData;

    case "toggleFavorites":
      const newFavoritesData = state.favorites.includes(vaultSlug) ? state.favorites.filter(single => single !== vaultSlug) : [...state.favorites, vaultSlug];
      storagePush(newFavoritesData, "favorites");
      return {
        ...state,
        favorites: newFavoritesData,
      } as SupStoredData;

    case "clearAllFilters":
      storagePush([], "filters");
      return {
        ...state,
        filters: [],
      };
    case "clearFilter":
      if (filterName === undefined) {
        throw new Error(`filterName props are missing: ”${type}”`);
      }
      const clearedFiltersData = state.filters.filter(currentFilter => currentFilter.name !== filterName);
      storagePush(clearedFiltersData, "filters");
      return {
        ...state,
        filters: clearedFiltersData,
      };
    case "setFilter":
      if (filter === undefined) {
        throw new Error(`filter prop is missing: ”${type}”`);
      }
      const newFiltersData = state.filters.filter(currentFilter => filter.name !== currentFilter.name);
      newFiltersData.push(filter);
      storagePush(newFiltersData, "filters");
      return {
        ...state,
        filters: newFiltersData,
      };
    case "toggleFavTokens":
      if (favToken === undefined) {
        throw new Error(`favToken prop is missing: ”${type}”`);
      }
      const newFavTokensData = checkFavToken(state.favTokens, favToken)
        ? state.favTokens.filter(singleToken => singleToken.tokenAddress !== favToken.tokenAddress || singleToken.chainId != favToken.chainId)
        : [...state.favTokens, favToken];
      storagePush(newFavTokensData, "favTokens");
      return {
        ...state,
        favTokens: newFavTokensData,
      } as SupStoredData;

    case "clearFavorites":
      storagePush([], "favorites");
      return {
        ...state,
        favorites: [],
      };

    case "clearCart":
      storagePush([], "cart");
      storagePush(undefined, "txCartValue");
      return {
        ...state,
        cart: [],
        txCartValue: undefined,
      };

    case "clearWithdrawCart":
      storagePush([] as SupStoredWithdrawItem[], "withdrawCart");
      storagePush(undefined, "withdrawalChainId");
      return {
        ...state,
        withdrawCart: [],
        withdrawalChainId: undefined,
      };
    case "setViewMode":
      if (viewMode === undefined) {
        throw new Error(`viewMode prop is missing: ”${type}”`);
      }
      storagePush(viewMode, "viewMode");
      return {
        ...state,
        viewMode,
      };
    case "toggleDust":
      const toggleDustValue = !state.showDust;
      storagePush(toggleDustValue, "showDust");
      return {
        ...state,
        showDust: toggleDustValue,
      };
    case "toggleFavFilter":
      const toggleFavFilter = !state.filterFavorites;
      storagePush(toggleFavFilter, "filterFavorites");
      return {
        ...state,
        filterFavorites: toggleFavFilter,
      };
    case "turnOffFavFilter":
      storagePush(false, "filterFavorites");
      return {
        ...state,
        filterFavorites: false,
      };
    case "setOverlay":
    case "closeOverlay":
      const overlayValue = overlay ? overlay : undefined;
      storagePush(overlayValue, "overlay");
      if (!overlayValue) {
        document.body.style.overflow = "auto";
      }
      return {
        ...state,
        overlay: overlayValue,
      };
    case "setTxSettings":
      const txSettingsValue = txSettings ? txSettings : defaultTxSettings;
      storagePush(txSettingsValue, "txSettings");
      return {
        ...state,
        txSettings: txSettingsValue,
      };
    case "setDataGridColumns":
      if (dataGridColumns === undefined) {
        throw new Error(`dataGridColumns prop is missing: ”${type}”`);
      }

      const newDataGridColumnsData = state.dataGridColumns.filter(currentDataGrid => currentDataGrid.grid !== dataGridColumns.grid);
      newDataGridColumnsData.push(dataGridColumns);
      storagePush(newDataGridColumnsData, "dataGridColumns");
      return {
        ...state,
        dataGridColumns: newDataGridColumnsData,
      };

    case "resetDataGridColumns":
      if (grid === undefined) {
        throw new Error(`dataGridColumns prop is missing: ”${type}”`);
      }

      const filteredDataGridColumns = state.dataGridColumns.filter(currentDataGrid => currentDataGrid.grid !== grid);
      storagePush(filteredDataGridColumns, "dataGridColumns");
      return {
        ...state,
        dataGridColumns: filteredDataGridColumns,
      };

    case "setTokenSelectorChains":
      const tokenSelectorChainsValue = tokenSelectorChains ? tokenSelectorChains : ([] as Networkish[]);
      storagePush(tokenSelectorChainsValue, "tokenSelectorChains");
      return {
        ...state,
        tokenSelectorChains: tokenSelectorChainsValue,
      };

    case "toggleIncludeRewardsInApy":
      const toggleIncludeRewardsInApyValue = !state.includeRewardsInApy;
      storagePush(toggleIncludeRewardsInApyValue, "includeRewardsInApy", true);
      return {
        ...state,
        includeRewardsInApy: toggleIncludeRewardsInApyValue,
      };

    case "setRewardTokensValuation":
      if (rewardTokenValuations === undefined) {
        throw new Error(`rewardTokenValuations prop is missing: ”${type}”`);
      }
      storagePush(rewardTokenValuations, "rewardTokenValuations");
      return {
        ...state,
        rewardTokenValuations: rewardTokenValuations,
      } as SupStoredData;

    case "clearTransmute":
      storagePush(undefined, "transmute");
      return {
        ...state,
        transmute: transmute,
      } as SupStoredData;

    case "setTransmute":
      if (transmute === undefined) {
        throw new Error(`transmute prop is missing: ”${type}”`);
      }
      storagePush(transmute, "transmute");
      return {
        ...state,
        transmute: transmute,
      } as SupStoredData;

    case "setTxCartValue":
      storagePush(txCartValue, "txCartValue");
      return {
        ...state,
        txCartValue: txCartValue,
      } as SupStoredData;

    default:
      throw new Error(`Unknown action called for the global reducer: ”${type}”`);
  }
};
